import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getAllProducts, getTopSoldProducts, getCompanyConfig, createTailoringOrder, createCustomer, getRecentCustomers, formatPhone } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { toast } from 'sonner';

const toNumber = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const money = (n) => Number(n || 0).toLocaleString('es-DO', { style: 'currency', currency: 'DOP' });

const isService = (product) => {
  const type = String(product?.product_type || product?.type || '').trim().toUpperCase();
  return type === 'SERVICIO' || type === 'SERVICE' || product?.track_inventory === false || product?.track_inventory === 'false' || product?.track_inventory === 0;
};

const defaultDueDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
};

const emptySubOrderForm = {
  product_id: '',
  product_name: '',
  item_price: '',
  customization: '',
  measurements: { pecho: '', cintura: '', cadera: '', largo: '', manga: '' },
  changes_requested: '',
  services: [],
  work_notes: '',
};

const TailoringOrderNew = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [products, setProducts]         = useState([]);
  const [topProducts, setTopProducts]   = useState([]);
  const [topServices, setTopServices]   = useState([]);
  const [cardSurchargePct, setCardSurchargePct] = useState(0);
  const [saving, setSaving]             = useState(false);
  const [productSearch, setProductSearch]       = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [serviceSearch, setServiceSearch]         = useState('');
  const [showServiceSearch, setShowServiceSearch] = useState(false);
  const [showPriceNote, setShowPriceNote]         = useState(() => sessionStorage.getItem('priceNoteDismissed') !== '1');
  const [paymentMethod, setPaymentMethod]         = useState('efectivo');

  // ── Customer ──────────────────────────────────────────────────────────
  const [recentCustomers, setRecentCustomers]           = useState([]);
  const [customerSearch, setCustomerSearch]             = useState('');
  const [customerResults, setCustomerResults]           = useState([]);
  const [customerSearching, setCustomerSearching]       = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showNewCustomerForm, setShowNewCustomerForm]   = useState(false);
  const [newCustomerData, setNewCustomerData]           = useState({ name: '', last_name: '', phone: '', email: '' });
  const [phoneWarning, setPhoneWarning]                 = useState(null);

  // ── Main form ─────────────────────────────────────────────────────────
  const [form, setForm] = useState({ customer_id: '', due_date: defaultDueDate(), initial_payment: '', notes: '' });
  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ── Sub-orders ────────────────────────────────────────────────────────
  const [subOrders, setSubOrders]               = useState([]);
  const [showSubOrderForm, setShowSubOrderForm] = useState(false);
  const [editingIdx, setEditingIdx]             = useState(null);
  const [soForm, setSoForm]                     = useState({ ...emptySubOrderForm });
  const setSoField = (k, v)                     => setSoForm(p => ({ ...p, [k]: v }));
  const setSoMeas  = (k, v)                     => setSoForm(p => ({ ...p, measurements: { ...p.measurements, [k]: v } }));
  const [showCustomization, setShowCustomization] = useState(false);

  // ── Derived ───────────────────────────────────────────────────────────
  const baseProducts     = useMemo(() => products.filter(p => !isService(p)), [products]);
  const serviceProducts  = useMemo(() => products.filter(p => isService(p)),  [products]);
  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return [];
    const q = productSearch.toLowerCase();
    return baseProducts.filter(p => p.name?.toLowerCase().includes(q)).slice(0, 8);
  }, [baseProducts, productSearch]);
  const filteredServices = useMemo(() => {
    if (!serviceSearch.trim()) return [];
    const q = serviceSearch.toLowerCase();
    return serviceProducts.filter(p => p.name?.toLowerCase().includes(q)).slice(0, 8);
  }, [serviceProducts, serviceSearch]);
  const CARD_FACTOR      = cardSurchargePct > 0 ? (1 + cardSurchargePct / 100) : 1;
  const cardPrice = (p) => cardSurchargePct > 0
    ? Math.round(toNumber(p.price) * CARD_FACTOR * 100) / 100
    : null;
  const totalAmount      = useMemo(() => subOrders.reduce((s, so) => s + so.subtotal, 0), [subOrders]);
  const confectionTotal  = useMemo(() => subOrders.reduce((s, so) => s + toNumber(so.item_price), 0), [subOrders]);
  const servicesTotalAll = useMemo(() => subOrders.reduce((s, so) => s + so.services_total, 0), [subOrders]);
  const withSurcharge    = (n) => Math.round(n * CARD_FACTOR * 100) / 100;
  const isCardPayment    = paymentMethod === 'tarjeta' && cardSurchargePct > 0;
  const effectiveTotal   = isCardPayment ? withSurcharge(totalAmount)      : totalAmount;
  const effectiveConf    = isCardPayment ? withSurcharge(confectionTotal)   : confectionTotal;
  const effectiveSvc     = isCardPayment ? withSurcharge(servicesTotalAll)  : servicesTotalAll;
  const soItemPrice      = useMemo(() => toNumber(soForm.item_price) + toNumber(soForm.customization), [soForm.item_price, soForm.customization]);
  const soServicesTotal  = useMemo(() => soForm.services.reduce((s, sv) => s + sv.subtotal, 0), [soForm.services]);
  const soSubtotal       = useMemo(() => soItemPrice + soServicesTotal, [soItemPrice, soServicesTotal]);

  useEffect(() => {
    (async () => {
      try {
        const prods = await getAllProducts();
        setProducts(Array.isArray(prods) ? prods : (prods?.products || []));
      } catch (e) {
        toast.error(e?.response?.data?.error || 'Error cargando productos');
      }
    })();
    getRecentCustomers().then(data => setRecentCustomers(Array.isArray(data) ? data : [])).catch(() => {});
    getCompanyConfig().then(cfg => setCardSurchargePct(parseFloat(cfg?.card_surcharge_pct || 0))).catch(() => {});
    getTopSoldProducts(5, user?.branch_id || '', 'products').then(setTopProducts).catch(() => {});
    getTopSoldProducts(3, user?.branch_id || '', 'services').then(setTopServices).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(() => searchCustomers(customerSearch), 350);
    return () => clearTimeout(t);
  }, [customerSearch]); // eslint-disable-line

  const searchCustomers = async (q) => {
    if (!q || q.length < 2) { setCustomerResults([]); return; }
    setCustomerSearching(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customers?q=${encodeURIComponent(q)}&per_page=8`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setCustomerResults(data.customers || data || []);
    } catch { setCustomerResults([]); }
    finally { setCustomerSearching(false); }
  };

  const [selectedCustomerData, setSelectedCustomerData] = React.useState(null);

  const selectCustomer = (c) => {
    setField('customer_id', c.id);
    setCustomerSearch(`${c.name} ${c.last_name}`);
    setShowCustomerDropdown(false);
    setCustomerResults([]);
    setSelectedCustomerData(c);
  };

  const handleCreateCustomer = async (confirmDuplicate = false) => {
    if (!newCustomerData.name || !newCustomerData.phone) {
      toast.error('Nombre y teléfono son requeridos'); return;
    }
    if (!isValidPhoneNumber(newCustomerData.phone)) {
      toast.error('Número de teléfono inválido'); return;
    }
    try {
      const payload = confirmDuplicate ? { ...newCustomerData, confirm_duplicate_phone: true } : newCustomerData;
      const created = await createCustomer(payload);
      selectCustomer(created);
      setShowNewCustomerForm(false);
      setPhoneWarning(null);
      setNewCustomerData({ name: '', last_name: '', phone: '', email: '' });
      toast.success('Cliente creado');
      getRecentCustomers().then(data => setRecentCustomers(Array.isArray(data) ? data : [])).catch(() => {});
    } catch (e) {
      const res = e?.response?.data;
      if (e?.response?.status === 409 && res?.warning === 'duplicate_phone') {
        setPhoneWarning(res.message); return;
      }
      toast.error(res?.error || 'Error creando cliente');
    }
  };

  // ── Sub-order handlers ────────────────────────────────────────────────
  const handleSoProductChange = (productId) => {
    const prod = baseProducts.find(p => p.id === productId);
    setSoForm(p => ({
      ...p,
      product_id:   productId,
      product_name: prod?.name || '',
      item_price:   String(Number(prod?.price || 0)),
      customization: '',
    }));
  };

  const addSoServiceById = (id) => {
    const svc = serviceProducts.find(p => p.id === id);
    if (!svc) return;
    const unitPrice = Number(svc.price || 0);
    setSoForm(p => {
      const existing = p.services.find(s => s.product_id === svc.id);
      const next = existing
        ? p.services.map(s => s.product_id !== svc.id ? s : { ...s, quantity: s.quantity + 1, subtotal: (s.quantity + 1) * s.unit_price })
        : [...p.services, { product_id: svc.id, name: svc.name, quantity: 1, unit_price: unitPrice, subtotal: unitPrice }];
      return { ...p, services: next };
    });
  };


  const removeSoService = (productId) =>
    setSoForm(p => ({ ...p, services: p.services.filter(s => s.product_id !== productId) }));

  const saveSubOrder = () => {
    if (!soForm.product_id)                        return toast.error('Selecciona un producto base');
    if (!soForm.item_price || toNumber(soForm.item_price) <= 0) return toast.error('El costo de confección debe ser mayor que 0');
    if (!soForm.changes_requested.trim())          return toast.error('Los cambios solicitados son obligatorios');
    const services_total   = soForm.services.reduce((s, sv) => s + sv.subtotal, 0);
    const effective_price  = toNumber(soForm.item_price) + toNumber(soForm.customization);
    const subtotal         = effective_price + services_total;
    const entry            = { ...soForm, item_price: effective_price, customization: toNumber(soForm.customization), services_total, subtotal };

    if (editingIdx !== null) {
      setSubOrders(p => p.map((so, i) => i === editingIdx ? entry : so));
    } else {
      setSubOrders(p => [...p, entry]);
    }
    setSoForm({ ...emptySubOrderForm });
    setShowCustomization(false);
    setShowSubOrderForm(false);
    setEditingIdx(null);
  };

  const editSubOrder = (idx) => {
    const so = subOrders[idx];
    setSoForm({ ...so, measurements: { ...so.measurements } });
    setShowCustomization(toNumber(so.customization) > 0);
    setEditingIdx(idx);
    setShowSubOrderForm(true);
  };

  const duplicateSubOrder = (idx) => {
    setSubOrders(p => [...p, { ...p[idx] }]);
    toast.success('Prenda duplicada');
  };

  const removeSubOrder = (idx) => setSubOrders(p => p.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!form.customer_id)    return toast.error('Selecciona un cliente');
    if (subOrders.length === 0) return toast.error('Agrega al menos una prenda');

    const anticipo   = parseFloat(form.initial_payment) || 0;
    const _effTotal  = isCardPayment ? withSurcharge(totalAmount) : totalAmount;
    const minPayment = _effTotal * 0.5;
    if (anticipo <= 0)          return toast.error('Debes ingresar el anticipo para crear el pedido');
    if (anticipo < minPayment - 0.009)  return toast.error(`El anticipo mínimo es ${money(minPayment)} (50% de ${money(_effTotal)})`);

    const anticiPoCash = isCardPayment ? Math.round(anticipo / CARD_FACTOR * 100) / 100 : anticipo;

    try {
      setSaving(true);
      const payload = {
        customer_id:       form.customer_id,
        delivery_due_date: form.due_date,
        notes:             form.notes,
        initial_payment:   anticiPoCash,
        sub_orders: subOrders.map(so => ({
          product_id:        so.product_id,
          item_price:        so.item_price,
          measurements:      so.measurements,
          changes_requested: so.changes_requested,
          services:          so.services.map(s => ({ product_id: s.product_id, quantity: s.quantity, unit_price: s.unit_price })),
          work_notes:        so.work_notes,
        })),
      };
      const created = await createTailoringOrder(payload);
      toast.success('Pedido creado — abriendo POS para cobrar el anticipo');
      navigate(`/pos?tailoring_order_id=${created.id}&initial_payment=${anticiPoCash.toFixed(2)}&payment_method=${paymentMethod}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Error creando pedido');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 space-y-6 max-w-4xl mx-auto" data-testid="tailoring-new-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Nuevo Pedido de Confección</h1>
        <p className="text-slate-600">El pedido queda en espera de producción. Sucursal y vendedor se toman del usuario logueado.</p>
      </div>

      {/* Cliente */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Cliente</CardTitle></CardHeader>
        <CardContent>
          <div className="relative">
            <Label>Buscar cliente</Label>
            <div className="relative mt-1">
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); if (!e.target.value) setField('customer_id', ''); }}
                onFocus={() => customerSearch.length >= 2 && setShowCustomerDropdown(true)}
                onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                placeholder="Buscar por nombre o teléfono..."
                className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 ${form.customer_id ? 'border-cyan-400 bg-cyan-50' : 'border-slate-200'}`}
              />
              {customerSearching && <span className="absolute right-3 top-2.5 text-xs text-slate-400">...</span>}
              {form.customer_id  && <span className="absolute right-3 top-2.5 text-cyan-500 text-xs">✓</span>}
            </div>

            {selectedCustomerData?.has_logo && (
              <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                <span>🖼️</span>
                <div>
                  <span className="font-medium">Logo disponible</span>
                  {selectedCustomerData.logo_notes && <span className="text-emerald-600 ml-2 text-xs">{selectedCustomerData.logo_notes}</span>}
                </div>
              </div>
            )}

            {showCustomerDropdown && customerResults.length > 0 && (
              <div className="absolute z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-52 overflow-y-auto">
                {customerResults.map(c => (
                  <button key={c.id} type="button" onMouseDown={() => selectCustomer(c)}
                    className="w-full text-left px-3 py-2 hover:bg-cyan-50 text-sm border-b border-slate-100 last:border-0">
                    <span className="font-medium">{c.name} {c.last_name}</span>
                    <span className="text-slate-400 ml-2 text-xs">{formatPhone(c.phone)}</span>
                  </button>
                ))}
                <button type="button" onMouseDown={() => { setShowCustomerDropdown(false); setShowNewCustomerForm(true); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm text-cyan-600 font-medium flex items-center gap-1">
                  <span>+</span> Crear nuevo cliente
                </button>
              </div>
            )}

            {showCustomerDropdown && customerSearch.length >= 2 && customerResults.length === 0 && !customerSearching && (
              <div className="absolute z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1">
                <button type="button" onMouseDown={() => { setShowCustomerDropdown(false); setShowNewCustomerForm(true); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm text-cyan-600 font-medium flex items-center gap-1">
                  <span>+</span> No encontrado — Crear cliente
                </button>
              </div>
            )}

            {showNewCustomerForm && (
              <div className="mt-2 p-3 border border-cyan-200 bg-cyan-50 rounded-lg space-y-2">
                <p className="text-xs font-semibold text-cyan-800">Nuevo cliente</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input placeholder="Nombre *" value={newCustomerData.name}
                    onChange={e => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                    className="border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400" />
                  <input placeholder="Apellido" value={newCustomerData.last_name}
                    onChange={e => setNewCustomerData({ ...newCustomerData, last_name: e.target.value })}
                    className="border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400" />
                  <div className="phone-input-container">
                    <PhoneInput international defaultCountry="DO"
                      value={newCustomerData.phone}
                      onChange={v => setNewCustomerData({ ...newCustomerData, phone: v || '' })} />
                  </div>
                  <input placeholder="Email" value={newCustomerData.email}
                    onChange={e => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                    className="border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400" />
                </div>
                {phoneWarning && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 space-y-2">
                    <div className="flex items-start gap-1.5">
                      <span className="text-base">⚠️</span>
                      <div>
                        <p className="font-semibold text-amber-900 text-xs">Teléfono duplicado</p>
                        <p className="text-xs text-amber-800">{phoneWarning}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setPhoneWarning(null)}
                        className="flex-1 text-xs border border-slate-200 rounded py-1 hover:bg-slate-50">Cancelar</button>
                      <button type="button" onClick={() => { setPhoneWarning(null); handleCreateCustomer(true); }}
                        className="flex-1 text-xs bg-amber-500 hover:bg-amber-600 text-white rounded py-1 font-medium">Guardar igual</button>
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  <button type="button" onClick={() => handleCreateCustomer(false)}
                    className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white text-xs font-medium py-1.5 rounded transition-colors">
                    Crear y seleccionar
                  </button>
                  <button type="button" onClick={() => { setShowNewCustomerForm(false); setPhoneWarning(null); }}
                    className="px-3 text-slate-500 hover:text-slate-700 text-xs border border-slate-200 rounded py-1.5">
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>

          {recentCustomers.length > 0 && !form.customer_id && !customerSearch && (
            <div className="mt-3">
              <p className="text-xs text-slate-400 mb-1.5">Recientes:</p>
              <div className="flex flex-wrap gap-1.5">
                {recentCustomers.map(c => (
                  <button key={c.id} type="button" onClick={() => selectCustomer(c)}
                    className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 hover:bg-cyan-100 hover:text-cyan-800 transition-colors border border-slate-200 hover:border-cyan-300">
                    {c.name} {c.last_name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Prendas (sub-pedidos) */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-lg">Prendas</CardTitle>
            {subOrders.length > 0 && (
              <p className="text-sm text-slate-500 mt-0.5">
                {subOrders.length} prenda{subOrders.length > 1 ? 's' : ''} — {money(totalAmount)}
                {cardSurchargePct > 0 && <span className="text-blue-600"> · {money(withSurcharge(totalAmount))} tarjeta</span>}
              </p>
            )}
          </div>
          {!showSubOrderForm && subOrders.length > 0 && (
            <Button type="button" size="sm"
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              onClick={() => { setSoForm({ ...emptySubOrderForm }); setEditingIdx(null); setShowSubOrderForm(true); }}>
              + Agregar prenda
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">

          {/* Lista de prendas agregadas */}
          {subOrders.map((so, idx) => (
            <div key={idx} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-cyan-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {String.fromCharCode(65 + idx)}
                  </span>
                  <div>
                    <p className="font-medium text-sm">{so.product_name}</p>
                    <p className="text-xs text-slate-500">
                      Confección: {money(so.item_price)}{cardSurchargePct > 0 && <span className="text-blue-500"> · {money(Math.round(so.item_price * (1 + cardSurchargePct / 100) * 100) / 100)} tarjeta</span>}
                      {so.services_total > 0 && <>{` + servicios: ${money(so.services_total)}`}{cardSurchargePct > 0 && <span className="text-blue-500"> · {money(Math.round(so.services_total * (1 + cardSurchargePct / 100) * 100) / 100)} tarjeta</span>}</>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <div className="font-semibold text-sm text-slate-700">{money(so.subtotal)}</div>
                    {cardSurchargePct > 0 && <div className="text-xs text-blue-600">{money(Math.round(so.subtotal * (1 + cardSurchargePct / 100) * 100) / 100)} tarjeta</div>}
                  </div>
                  <div className="flex gap-2 text-xs">
                    <button type="button" onClick={() => editSubOrder(idx)}
                      className="text-slate-400 hover:text-slate-700 font-medium">Editar</button>
                    <button type="button" onClick={() => duplicateSubOrder(idx)}
                      className="text-cyan-400 hover:text-cyan-600 font-medium">Duplicar</button>
                    <button type="button" onClick={() => removeSubOrder(idx)}
                      className="text-rose-400 hover:text-rose-600 font-medium">Quitar</button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {subOrders.length === 0 && !showSubOrderForm && (
            <button type="button"
              onClick={() => { setSoForm({ ...emptySubOrderForm }); setEditingIdx(null); setShowSubOrderForm(true); }}
              className="w-full border-2 border-dashed border-slate-300 rounded-lg py-6 text-slate-400 hover:border-cyan-400 hover:text-cyan-600 transition-colors text-sm font-medium">
              + Agregar primera prenda
            </button>
          )}

          {subOrders.length > 0 && !showSubOrderForm && (
            <button type="button"
              onClick={() => { setSoForm({ ...emptySubOrderForm }); setEditingIdx(null); setShowSubOrderForm(true); }}
              className="w-full border border-dashed border-slate-200 rounded-lg py-3 text-slate-400 hover:border-cyan-400 hover:text-cyan-500 transition-colors text-sm">
              + Agregar otra prenda
            </button>
          )}

          {/* Formulario de prenda */}
          {showSubOrderForm && (
            <div className="border-2 border-cyan-200 rounded-xl p-4 bg-cyan-50 space-y-4">
              <p className="text-sm font-semibold text-cyan-800">
                {editingIdx !== null
                  ? `Editando prenda ${String.fromCharCode(65 + editingIdx)}`
                  : `Nueva prenda ${String.fromCharCode(65 + subOrders.length)}`}
              </p>

              {/* Producto + costo */}
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Modelo base *</Label> 
                 
                  {/* Top 5 chips */}
                  {topProducts.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {topProducts.map(p => {
                        const cp = cardPrice(p);
                        const selected = soForm.product_id === p.id;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => { handleSoProductChange(p.id); setShowProductSearch(false); setProductSearch(''); }}
                            className={`flex flex-col items-start px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
                              selected
                                ? 'bg-cyan-500 text-white border-cyan-500'
                                : 'bg-white text-slate-700 border-slate-200 hover:border-cyan-400 hover:text-cyan-700'
                            }`}
                          >
                            <span>{p.name}</span>
                            <span className={`text-xs font-normal mt-0.5 ${selected ? 'text-cyan-100' : 'text-slate-400'}`}>
                              {money(p.price)}
                              {cp && <span className={selected ? ' text-cyan-200' : ' text-slate-400'}> · {money(cp)} tarjeta</span>}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Buscador de otros productos */}
                  <div className="relative">
                    <input
                      type="text"
                      value={productSearch}
                      onChange={e => { setProductSearch(e.target.value); setShowProductSearch(true); }}
                      onFocus={() => setShowProductSearch(true)}
                      onBlur={() => setTimeout(() => setShowProductSearch(false), 200)}
                      placeholder="Buscar otro producto..."
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 bg-white"
                    />
                    {showProductSearch && filteredProducts.length > 0 && (
                      <div className="absolute z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-52 overflow-y-auto">
                        {filteredProducts.map(p => {
                          const cp = cardPrice(p);
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onMouseDown={() => { handleSoProductChange(p.id); setProductSearch(''); setShowProductSearch(false); }}
                              className="w-full text-left px-3 py-2 hover:bg-cyan-50 border-b border-slate-100 last:border-0"
                            >
                              <span className="text-sm font-medium text-slate-800">{p.name}</span>
                              <span className="text-xs text-slate-400 ml-2">{money(p.price)} efectivo{cp ? ` · ${money(cp)} tarjeta` : ''}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Producto seleccionado */}
                  {soForm.product_id && (() => {
                    const prod = baseProducts.find(p => p.id === soForm.product_id);
                    const cp = prod ? cardPrice(prod) : null;
                    return prod ? (
                      <>
                        <div className="flex items-center justify-between bg-cyan-500 rounded-lg px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className="text-white text-base">✓</span>
                            <span className="font-semibold text-white text-sm">{prod.name}</span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-white">{money(prod.price)} <span className="text-xs font-normal text-cyan-100">efectivo</span></p>
                            {cp && <p className="text-xs text-cyan-100">{money(cp)} <span className="text-cyan-200">tarjeta</span></p>}
                          </div>
                        </div>
                        <span className="text-xs text-slate-400">ℹ️ Pago en <strong className="text-slate-700">EFECTIVO</strong> incluye un {cardSurchargePct}% de descuento · Pago con <strong className="text-slate-700">Tarjeta</strong> no incluye descuento, al momento de facturar se le agrega el <strong className="text-slate-700">ITBIS</strong>.</span>
                      </>
                    ) : null;
                  })()}
                </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2 h-6">
                  <input
                    type="checkbox"
                    id="chk-customization"
                    checked={showCustomization}
                    onChange={e => {
                      setShowCustomization(e.target.checked);
                      if (!e.target.checked) setSoField('customization', '');
                    }}
                    className="w-4 h-4 accent-cyan-500 cursor-pointer"
                  />
                  <label htmlFor="chk-customization" className="text-sm font-medium text-slate-700 cursor-pointer">
                    Personalización
                  </label>
                </div>
                {showCustomization && (
                  <Input
                    type="number" min="0" step="0.01"
                    value={soForm.customization}
                    onChange={e => setSoField('customization', e.target.value)}
                    placeholder="0.00"
                    className="bg-white"
                    autoFocus
                  />
                )}
              </div>
              </div>{/* end space-y-3 producto */}

              {/* Medidas */}
              <div>
                <Label className="mb-2 block">Medidas (cm)</Label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {['pecho', 'cintura', 'cadera', 'largo', 'manga'].map(k => (
                    <div key={k} className="space-y-1">
                      <label className="text-xs text-slate-500 capitalize">{k}</label>
                      <Input
                        type="number" min="0" step="0.1"
                        value={soForm.measurements[k]}
                        onChange={e => setSoMeas(k, e.target.value)}
                        placeholder="0"
                        className="bg-white"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Cambios solicitados */}
              <div className="space-y-1">
                <Label>Cambios solicitados <span className="text-rose-500 text-xs">*</span></Label>
                <Textarea
                  value={soForm.changes_requested}
                  onChange={e => setSoField('changes_requested', e.target.value)}
                  placeholder="Describe los cambios o ajustes solicitados..."
                  className="bg-white text-sm"
                  rows={2}
                />
              </div>

              {/* Servicios adicionales */}
              <div className="space-y-2">
                <Label>Servicios</Label>

                {/* Top 3 chips */}
                {topServices.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {topServices.map(s => {
                      const cp = cardPrice(s);
                      const already = soForm.services.some(sv => sv.product_id === s.id);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => addSoServiceById(s.id)}
                          disabled={already}
                          className={`flex flex-col items-start px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
                            already
                              ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                              : 'bg-white text-slate-700 border-slate-200 hover:border-cyan-400 hover:text-cyan-700'
                          }`}
                        >
                          <span>{s.name}</span>
                          <span className="text-xs font-normal mt-0.5 text-slate-400">
                            {money(s.price)}
                            {cp && <span> · {money(cp)} tarjeta</span>}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Buscador */}
                <div className="relative">
                  <input
                    type="text"
                    value={serviceSearch}
                    onChange={e => { setServiceSearch(e.target.value); setShowServiceSearch(true); }}
                    onFocus={() => setShowServiceSearch(true)}
                    onBlur={() => setTimeout(() => setShowServiceSearch(false), 200)}
                    placeholder="Buscar otro servicio..."
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 bg-white"
                  />
                  {showServiceSearch && filteredServices.length > 0 && (
                    <div className="absolute z-50 w-full bg-white border border-slate-200 rounded-lg shadow-lg mt-1 max-h-52 overflow-y-auto">
                      {filteredServices.map(s => {
                        const cp = cardPrice(s);
                        const already = soForm.services.some(sv => sv.product_id === s.id);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onMouseDown={() => {
                              if (!already) addSoServiceById(s.id);
                              setServiceSearch('');
                              setShowServiceSearch(false);
                            }}
                            className={`w-full text-left px-3 py-2 border-b border-slate-100 last:border-0 ${already ? 'opacity-40 cursor-not-allowed' : 'hover:bg-cyan-50'}`}
                          >
                            <span className="text-sm font-medium text-slate-800">{s.name}</span>
                            <span className="text-xs text-slate-400 ml-2">{money(s.price)} efectivo{cp ? ` · ${money(cp)} tarjeta` : ''}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {soForm.services.length > 0 && (
                  <div className="space-y-1">
                    {soForm.services.map(s => {
                      const cp = cardSurchargePct > 0 ? Math.round(s.unit_price * (1 + cardSurchargePct / 100) * 100) / 100 : null;
                      return (
                        <div key={s.product_id} className="flex items-center justify-between bg-cyan-500 rounded-lg px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <span className="text-white text-base">✓</span>
                            <span className="font-semibold text-white text-sm">
                              {s.name}{s.quantity > 1 && <span className="ml-1 text-cyan-100 font-normal">×{s.quantity}</span>}
                            </span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-sm font-medium text-white">{money(s.subtotal)} <span className="text-xs font-normal text-cyan-100">efectivo</span></p>
                              {cp && <p className="text-xs text-cyan-100">{money(cp * s.quantity)} <span className="text-cyan-200">tarjeta</span></p>}
                            </div>
                            <button type="button" onClick={() => removeSoService(s.product_id)}
                              className="text-cyan-200 hover:text-white text-sm leading-none">✕</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <Label>Notas <span className="text-slate-400 font-normal text-xs">(opcional)</span></Label>
                <Textarea
                  value={soForm.work_notes}
                  onChange={e => setSoField('work_notes', e.target.value)}
                  placeholder="Instrucciones específicas para esta prenda..."
                  className="bg-white text-sm"
                  rows={2}
                />
              </div>

              {cardSurchargePct > 0 && showPriceNote && (
                <div className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-500">
                  <span>ℹ️ Pago en <strong className="text-slate-700">EFECTIVO</strong> incluye un {cardSurchargePct}% de descuento · Pago con <strong className="text-slate-700">Tarjeta</strong> se le agrega el ITBIS</span>
                  <button type="button" onClick={() => { setShowPriceNote(false); sessionStorage.setItem('priceNoteDismissed', '1'); }}
                    className="flex-shrink-0 text-slate-400 hover:text-slate-600 text-sm leading-none">✕</button>
                </div>
              )}

              {soSubtotal > 0 && (() => {
                const showCard = cardSurchargePct > 0;
                const cardItem  = (n) => Math.round(n * (1 + cardSurchargePct / 100) * 100) / 100;
                return (
                  <div className="rounded-lg border border-slate-200 overflow-hidden text-sm">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 text-xs text-slate-500 uppercase">
                          <th className="text-left px-3 py-1.5 font-medium">Concepto</th>
                          <th className="text-right px-3 py-1.5 font-medium">Efectivo</th>
                          {showCard && <th className="text-right px-3 py-1.5 font-medium">Tarjeta</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {soItemPrice > 0 && (
                          <tr className="border-t border-slate-100">
                            <td className="px-3 py-1.5 text-slate-600">Confección</td>
                            <td className="px-3 py-1.5 text-right text-slate-700">{money(soItemPrice)}</td>
                            {showCard && <td className="px-3 py-1.5 text-right text-slate-500">{money(cardItem(soItemPrice))}</td>}
                          </tr>
                        )}
                        {soServicesTotal > 0 && (
                          <tr className="border-t border-slate-100">
                            <td className="px-3 py-1.5 text-slate-600">Servicios</td>
                            <td className="px-3 py-1.5 text-right text-slate-700">{money(soServicesTotal)}</td>
                            {showCard && <td className="px-3 py-1.5 text-right text-slate-500">{money(cardItem(soServicesTotal))}</td>}
                          </tr>
                        )}
                        <tr className="border-t-2 border-slate-300 bg-slate-50">
                          <td className="px-3 py-2 font-bold text-slate-900">Subtotal</td>
                          <td className="px-3 py-2 text-right font-bold text-slate-900">{money(soSubtotal)}</td>
                          {showCard && <td className="px-3 py-2 text-right font-bold text-cyan-700">{money(cardItem(soSubtotal))}</td>}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              <div className="flex justify-center gap-2 pt-1">
                <Button type="button" onClick={saveSubOrder} className="bg-cyan-500 hover:bg-cyan-600 text-white w-40 h-11">
                  {editingIdx !== null ? 'Guardar cambios' : 'Agregar prenda'}
                </Button>
                <Button type="button" variant="outline" className="w-40 h-11"
                  onClick={() => { setShowSubOrderForm(false); setEditingIdx(null); setSoForm({ ...emptySubOrderForm }); setShowCustomization(false); }}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fecha de entrega */}
      <Card>
        <CardHeader><CardTitle className="text-lg">Fecha de entrega</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-1 max-w-xs">
            <Input type="date" value={form.due_date} onChange={e => setField('due_date', e.target.value)} />
            <p className="text-xs text-slate-500">Si no se cambia, queda a 7 días.</p>
          </div>
        </CardContent>
      </Card>

      {/* Notas generales */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Notas generales <span className="text-sm font-normal text-slate-400">(opcional)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={form.notes}
            onChange={e => setField('notes', e.target.value)}
            placeholder="Observaciones generales del pedido..."
            rows={3}
          />
        </CardContent>
      </Card>

      {/* Resumen de pago */}
      {totalAmount > 0 && (() => {
        const minPayment  = effectiveTotal * 0.5;
        const anticipo    = parseFloat(form.initial_payment) || 0;
        const balance     = effectiveTotal - anticipo;
        const isValid     = anticipo >= minPayment - 0.009;
        const hasAnticipo = anticipo > 0;
        return (
          <Card className={`border-2 ${isValid ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`}>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">💰 Resumen de pago</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">

              {/* Selector método de pago */}
              {cardSurchargePct > 0 && (
                <div className="flex gap-2">
                  {[
                    { value: 'efectivo', label: '💵 Efectivo' },
                    { value: 'tarjeta',  label: '💳 Tarjeta'  },
                  ].map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => { setPaymentMethod(opt.value); setField('initial_payment', ''); }}
                      className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        paymentMethod === opt.value
                          ? 'bg-cyan-500 text-white border-cyan-500'
                          : 'bg-white text-slate-600 border-slate-300 hover:border-cyan-400 hover:text-cyan-600'
                      }`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              {/* KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
                  <p className="text-xs text-slate-500 mb-1">Total confección</p>
                  <p className="text-xl font-bold text-slate-700" style={{ fontFamily: 'JetBrains Mono' }}>{money(effectiveConf)}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
                  <p className="text-xs text-slate-500 mb-1">Servicios adicionales</p>
                  <p className="text-xl font-bold text-slate-700" style={{ fontFamily: 'JetBrains Mono' }}>{money(effectiveSvc)}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border-2 border-slate-900 text-center">
                  <p className="text-xs text-slate-500 mb-1">Total general</p>
                  <p className="text-xl font-bold text-slate-900" style={{ fontFamily: 'JetBrains Mono' }}>{money(effectiveTotal)}</p>
                </div>
              </div>

              {/* Anticipo */}
              <div className="bg-white rounded-xl p-4 border border-amber-200 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Anticipo mínimo requerido (50%)</span>
                  <span className="font-bold text-amber-600" style={{ fontFamily: 'JetBrains Mono' }}>{money(minPayment)}</span>
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-slate-500">Anticipo a cobrar *</label>
                  <div className="flex gap-2">
                    {[
                      { label: '50% mín.', value: effectiveTotal * 0.5 },
                      { label: '75%',      value: effectiveTotal * 0.75 },
                      { label: '100%',     value: effectiveTotal },
                    ].map(opt => (
                      <button
                        key={opt.label}
                        type="button"
                        onClick={() => setField('initial_payment', opt.value.toFixed(2))}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                          parseFloat(form.initial_payment) === parseFloat(opt.value.toFixed(2))
                            ? 'bg-cyan-500 text-white border-cyan-500'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-cyan-400 hover:text-cyan-600'
                        }`}
                      >
                        {opt.label}<br />
                        <span className="font-mono">{money(opt.value)}</span>
                      </button>
                    ))}
                  </div>
                  <input
                    type="number" min="0" step="0.01"
                    value={form.initial_payment}
                    onChange={e => setField('initial_payment', e.target.value)}
                    placeholder={`Mínimo ${money(minPayment)}`}
                    max={effectiveTotal}
                    className={`w-full border-2 rounded-lg px-3 py-2 text-lg font-mono focus:outline-none focus:ring-2 ${
                      isValid     ? 'border-green-400 focus:ring-green-400 bg-green-50' :
                      hasAnticipo ? 'border-rose-400 focus:ring-rose-400 bg-rose-50'   :
                                    'border-slate-200 focus:ring-cyan-400'
                    }`}
                  />
                  {hasAnticipo && !isValid && (
                    <p className="text-xs text-rose-600 font-medium">⚠️ El anticipo debe ser mínimo {money(minPayment)} — faltan {money(minPayment - anticipo)}</p>
                  )}
                  {isValid && (
                    <p className="text-xs text-green-600 font-medium">✅ Anticipo válido — balance restante: {money(balance)}</p>
                  )}
                  {!hasAnticipo && (
                    <p className="text-xs text-amber-700">El pedido no se puede crear sin el pago del 50% mínimo.</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      <div className="flex gap-2">
        <Button onClick={submit} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar pedido'}
        </Button>
        <Button variant="outline" onClick={() => navigate('/tailoring-orders')} disabled={saving}>
          Cancelar
        </Button>
      </div>

      <style>{`
        .phone-input-container .PhoneInput {
          display: flex; align-items: center; height: 34px;
          border: 1px solid #e2e8f0; border-radius: 6px;
          background: white; padding: 0 8px; gap: 6px; font-size: 14px;
        }
        .phone-input-container .PhoneInput:focus-within {
          border-color: #06b6d4;
          box-shadow: 0 0 0 2px rgba(6,182,212,0.25);
        }
        .phone-input-container .PhoneInputInput {
          flex: 1; border: none; outline: none;
          background: transparent; font-size: 13px; color: #0f172a;
        }
        .phone-input-container .PhoneInputCountrySelect {
          border: none; background: transparent; cursor: pointer;
        }
        .phone-input-container .PhoneInputCountryIcon { width: 18px; height: 13px; }
      `}</style>
    </div>
  );
};

export default TailoringOrderNew;
